import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
  IconButton,
  Drawer,
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  Close,
  FileDownloadOutlined,
  ShowChart,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { vpaAPI } from '../../services/api';
import {
  EmptyState,
  ScoreHistoryCard,
  exportDrawerCapture,
  extractVisibleChartTooltip,
  fmt,
  fmtBn,
  getDisplayText,
  getDisplayTone,
  getSectorLabel,
  renderExportChartImage,
} from './shared';
import { colors, status } from '../../theme';

export default function TickerDrawer({ open, onClose, ticker, initialRow = null }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  useEffect(() => {
    if (!open || !ticker) return;
    setLoading(true);
    vpaAPI.getTicker(ticker)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, ticker]);

  const history = data?.history || [];
  const chartData = [...history].reverse().map((d) => ({
    date: d.scan_date?.slice(5) || d.date?.slice(5),
    score: d.vpa_score,
    setup: d.setup_score,
    penalty: d.penalty_score,
  }));

  const latest = history.length > 0 ? history[0] : null;
  const companyName = data?.companyName || initialRow?.companyName || null;
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const sectorLabel = getSectorLabel(initialRow, lang);
  const display = latest?.display || {};
  const headerDisplay = display.header || {};
  const actionDisplay = headerDisplay.action || null;
  const readyBadge = headerDisplay.ready_badge || null;
  const volumeBadge = headerDisplay.volume_badge || null;
  const closeBadge = headerDisplay.close_badge || null;
  const marketSnapshotItems = display.market_snapshot || [];
  const snapshotByLabel = Object.fromEntries(marketSnapshotItems.map((item) => [item.label_key, item]));
  const quickReadItems = display.quick_read || [];
  const tradingPlanDisplay = display.trading_plan || {};
  const trendBackdropItems = display.trend_backdrop || [];
  const marketRelativeItems = display.market_relative || [];
  const riskFlagItems = display.risk_flags || [];
  const activeSignalItems = display.active_signals || [];
  const scoreHistoryLabels = useMemo(() => ({
    score: t('vpa.table.vpaScore'),
    setup: t('vpa.table.setupScore'),
    penalty: t('vpa.table.penalty'),
  }), [t]);

  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || !latest || exportingImage) return;

    try {
      setExportingImage(true);
      const chartSectionNode = drawerCaptureRef.current.querySelector('[data-export-chart="true"]');
      const exportTooltip = extractVisibleChartTooltip(chartSectionNode);
      const measuredWidth = Math.max(360, Math.ceil(chartSectionNode?.getBoundingClientRect().width || drawerScrollRef.current.clientWidth || 440));
      const chartReplacement = chartData.length > 1
        ? await renderExportChartImage({
            chartData,
            title: t('vpa.dialog.scoreHistory'),
            labels: scoreHistoryLabels,
            width: measuredWidth,
            exportTooltip,
          })
        : null;
      const asOfDate = latest.scan_date || latest.date || new Date().toISOString().slice(0, 10);
      await exportDrawerCapture({
        captureNode: drawerCaptureRef.current,
        scrollNode: drawerScrollRef.current,
        fileName: `${ticker || 'vpa'}-drawer-${asOfDate}.png`,
        chartReplacement,
      });
    } catch (error) {
      console.error('Failed to export VPA drawer image', error);
    } finally {
      setExportingImage(false);
    }
  }, [chartData, exportingImage, latest, scoreHistoryLabels, t, ticker]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 520 }, bgcolor: colors.gray[50] } }}
    >
      <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: colors.gray[50] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.6, bgcolor: alpha(colors.background.paper, 0.7), borderBottom: `1px solid ${colors.gray[100]}` }}>
          <Box data-export-header-left="true" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 0.75,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{ flex: 1, bgcolor: colors.gray[800], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 900, color: colors.background.paper, fontSize: '0.88rem', lineHeight: 1, letterSpacing: '0.02em' }}>{ticker}</Typography>
              </Box>
              <Box sx={{ height: 12, bgcolor: status.purple.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 800, color: colors.background.paper, fontSize: '0.52rem', letterSpacing: '0.12em', lineHeight: 1 }}>VPA</Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 850,
                    fontSize: '1rem',
                    color: colors.gray[900],
                    lineHeight: 1.15,
                    letterSpacing: '-0.015em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title={companyName || ticker}
                >
                  {companyName || ticker}
                </Typography>
              </Box>
              {latest && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, minWidth: 0 }}>
                  {sectorLabel !== '—' && (
                    <Box sx={{
                      flexShrink: 0,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '6px',
                      bgcolor: alpha(colors.info.main, 0.1),
                      color: colors.info.dark,
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      letterSpacing: '-0.005em',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 220,
                    }}>
                      {sectorLabel}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-html2canvas-ignore="true" data-export-header-toolbar="true">
            <Tooltip title={exportingImage ? t('common.exportingImage', 'Đang xuất ảnh...') : t('common.exportImage', 'Xuất ảnh')} arrow>
              <span>
                <IconButton
                  onClick={handleExportDrawerImage}
                  disabled={!latest || loading || exportingImage}
                  size="small"
                  sx={{
                    border: `1px solid ${colors.gray[200]}`,
                    bgcolor: colors.background.paper,
                    color: colors.gray[600],
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    '&:hover': { bgcolor: colors.gray[50], borderColor: colors.gray[400] },
                  }}
                >
                  {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={{ border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, color: colors.gray[600], boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', '&:hover': { bgcolor: colors.gray[50], borderColor: colors.gray[400] } }}><Close sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </Box>
        <Divider sx={{ borderColor: colors.gray[100] }} />

        <Box ref={drawerScrollRef} data-export-scroll-root="true" sx={{ px: 2.5, pb: 3, pt: 2, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}` }}>
                    <Skeleton variant="text" width="55%" height={12} sx={{ mb: 0.8 }} />
                    <Skeleton variant="text" width="50%" height={32} />
                  </Box>
                ))}
              </Box>
              <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: 1 }} />
            </Box>
          ) : !latest ? (
            <EmptyState Icon={ShowChart} title={t('vpa.noData')} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1.9rem', fontWeight: 900, color: colors.gray[800], fontFamily: 'monospace', lineHeight: 1 }}>
                      {fmt(latest.close)}
                    </Typography>
                    {actionDisplay && (
                      <Chip
                        label={getDisplayText(actionDisplay, 'value', t)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: getDisplayTone(actionDisplay.tone).bg, color: getDisplayTone(actionDisplay.tone).color }}
                      />
                    )}
                    {readyBadge && (
                      <Chip
                        label={getDisplayText(readyBadge, 'value', t)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.58rem', fontWeight: 800, bgcolor: getDisplayTone(readyBadge.tone).bg, color: getDisplayTone(readyBadge.tone).color }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.68rem', color: colors.gray[500], mt: 0.4, maxWidth: 360 }}>
                    {getDisplayText(actionDisplay, 'sub', t)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {volumeBadge && <Chip label={`${getDisplayText(volumeBadge, 'label', t)}: ${getDisplayText(volumeBadge, 'value', t)}`} size="small" sx={{ height: 22, fontSize: '0.58rem', fontWeight: 700, bgcolor: getDisplayTone(volumeBadge.tone).bg, color: getDisplayTone(volumeBadge.tone).color }} />}
                  {closeBadge && <Chip label={`${getDisplayText(closeBadge, 'label', t)}: ${getDisplayText(closeBadge, 'value', t)}`} size="small" sx={{ height: 22, fontSize: '0.58rem', fontWeight: 700, bgcolor: getDisplayTone(closeBadge.tone).bg, color: getDisplayTone(closeBadge.tone).color }} />}
                </Box>
              </Box>

              <Box sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}` }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                    {t('vpa.dialog.marketSnapshotOn', { date: latest.scan_date || latest.date })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: '1px', bgcolor: colors.gray[100] }}>
                  {[
                    { label: t('vpa.dialog.currentPrice'), value: fmt(latest.close), sub: latest.entry_ref ? `${t('vpa.dialog.refPrice')}: ${fmt(latest.entry_ref)}` : null, color: colors.gray[800] },
                    { label: t('vpa.dialog.avgLiquidity'), value: fmtBn(latest.avg_value_20d), sub: companyName || null, color: colors.gray[600] },
                    { label: t('vpa.dialog.sessionVolume'), value: latest.volume ? fmt(latest.volume) : '—', sub: getDisplayText(snapshotByLabel['vpa.dialog.sessionVolume'], 'sub', t, null), color: getDisplayTone(snapshotByLabel['vpa.dialog.sessionVolume']?.tone).color },
                    { label: t('vpa.dialog.volumeRatio'), value: latest.vol_ratio != null ? `${latest.vol_ratio.toFixed(2)}×` : '—', sub: getDisplayText(snapshotByLabel['vpa.dialog.volumeRatio'], 'sub', t, null), color: getDisplayTone(snapshotByLabel['vpa.dialog.volumeRatio']?.tone).color },
                    { label: t('vpa.dialog.closePosition'), value: getDisplayText(snapshotByLabel['vpa.dialog.closePosition'], 'value', t), sub: getDisplayText(snapshotByLabel['vpa.dialog.closePosition'], 'sub', t, null), color: getDisplayTone(snapshotByLabel['vpa.dialog.closePosition']?.tone).color },
                    { label: t('vpa.dialog.distCount15'), value: latest.distribution_count_15 ?? '—', sub: getDisplayText(snapshotByLabel['vpa.dialog.distCount15'], 'sub', t, null), color: getDisplayTone(snapshotByLabel['vpa.dialog.distCount15']?.tone).color },
                  ].map(({ label, value, sub, color }) => (
                    <Box key={label} sx={{ bgcolor: colors.background.paper, px: 1.2, py: 0.85 }}>
                      <Typography sx={{ fontSize: '0.55rem', color: colors.gray[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color, fontFamily: 'monospace', lineHeight: 1.3 }}>{value}</Typography>
                      {sub && <Typography sx={{ fontSize: '0.56rem', color: colors.gray[400], mt: 0.25 }}>{sub}</Typography>}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}` }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                    {t('vpa.dialog.quickRead')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: '1px', bgcolor: colors.gray[200] }}>
                  {quickReadItems.map((item, index) => (
                    <Box key={`${item.label_key || 'quick'}-${index}`} sx={{ bgcolor: colors.background.paper, px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{getDisplayText(item, 'label', t)}</Typography>
                      <Typography sx={{ fontSize: '0.96rem', fontWeight: 800, color: getDisplayTone(item.tone).color, lineHeight: 1.25 }}>{getDisplayText(item, 'value', t)}</Typography>
                      {getDisplayText(item, 'sub', t, null) && <Typography sx={{ fontSize: '0.56rem', color: colors.gray[400], mt: 0.2 }}>{getDisplayText(item, 'sub', t, null)}</Typography>}
                    </Box>
                  ))}
                </Box>
              </Box>

              {activeSignalItems.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: colors.gray[500], mr: 0.5 }}>
                    {t('vpa.dialog.signals')}
                  </Typography>
                  {activeSignalItems.map((item, index) => (
                    <Chip key={`${item.value_key || 'signal'}-${index}`} label={getDisplayText(item, 'value', t)} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: getDisplayTone(item.tone).bg, color: getDisplayTone(item.tone).color, fontWeight: 700 }} />
                  ))}
                </Box>
              )}

              {(latest.entry_low || latest.stop_loss || latest.target_1) && (
                  <Box sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}` }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                      {t('vpa.dialog.tradingPlan')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: '1px', bgcolor: colors.gray[100] }}>
                    {[
                      { key: 'entry', value: latest.entry_low != null && latest.entry_high != null ? `${fmt(latest.entry_low)} – ${fmt(latest.entry_high)}` : '—' },
                      { key: 'stop_loss', value: latest.stop_loss != null ? fmt(latest.stop_loss) : '—' },
                      { key: 'target_1', value: latest.target_1 != null ? fmt(latest.target_1) : '—' },
                      { key: 'target_2', value: latest.target_2 != null ? fmt(latest.target_2) : '—' },
                      { key: 'rr_target_2', value: getDisplayText(tradingPlanDisplay.rr_target_2, 'value', t) },
                      { key: 'risk_pct', value: getDisplayText(tradingPlanDisplay.risk_pct, 'value', t) },
                      { key: 'entry_ref', value: latest.entry_ref != null ? fmt(latest.entry_ref) : '—' },
                      { key: 'fail_if', value: getDisplayText(tradingPlanDisplay.fail_if, 'value', t) },
                    ].map(({ key, value }) => {
                      const item = tradingPlanDisplay[key] || null;
                      return (
                        <Box key={key} sx={{ bgcolor: colors.background.paper, px: 1, py: 0.7 }}>
                          <Typography sx={{ fontSize: '0.55rem', color: colors.gray[400], fontWeight: 600 }}>{getDisplayText(item, 'label', t)}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: getDisplayTone(item?.tone).color, fontFamily: 'monospace', lineHeight: 1.3 }}>{value}</Typography>
                          {getDisplayText(item, 'sub', t, null) && <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: getDisplayTone(item?.tone).color, fontFamily: 'monospace', opacity: 0.75, lineHeight: 1.2 }}>{getDisplayText(item, 'sub', t, null)}</Typography>}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {(latest.buy_zone_ok != null || latest.anti_trap_ok != null) && (
                  <Box sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}` }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                      {t('vpa.dialog.readyGates')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: '1px', bgcolor: colors.gray[100] }}>
                    {[
                      { key: 'buyZone', ok: latest.buy_zone_ok },
                      { key: 'antiTrap', ok: latest.anti_trap_ok },
                      { key: 'structure', ok: latest.structure_ok },
                      { key: 'timing', ok: latest.timing_ok },
                    ].map((gate) => (
                        <Box key={gate.key} sx={{ bgcolor: colors.background.paper, px: 1.2, py: 1, textAlign: 'center' }}>
                        {gate.ok
                          ? <CheckCircle sx={{ fontSize: 20, color: colors.success.main }} />
                          : <Cancel sx={{ fontSize: 20, color: colors.error.main }} />}
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: gate.ok ? colors.success.main : colors.error.main, mt: 0.3 }}>
                          {t(`vpa.dialog.gate.${gate.key}`)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.52rem', color: colors.gray[400], mt: 0.2 }}>
                          {t(`vpa.dialog.gate.${gate.key}Desc`)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {(trendBackdropItems.length > 0 || marketRelativeItems.length > 0 || riskFlagItems.length > 0) && (
                  <Box sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}` }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                      {t('vpa.dialog.structureContext')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, p: 1.2 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: colors.gray[500], mb: 0.65, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('vpa.dialog.trendBackdrop')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {trendBackdropItems.map((item) => (
                          <Tooltip key={`${item.value_key}-${item.tooltip_key || ''}`} title={item.tooltip_key ? t(item.tooltip_key) : ''} arrow>
                            <Chip
                              label={getDisplayText(item, 'value', t)}
                              size="small"
                              sx={{ fontSize: '0.6rem', height: 22, fontWeight: 700, bgcolor: getDisplayTone(item.tone).bg, color: getDisplayTone(item.tone).color }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: colors.gray[500], mb: 0.65, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('vpa.dialog.marketRelative')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {marketRelativeItems.map((item) => (
                          <Tooltip key={`${item.value_key || item.value_text}-${item.tooltip_key || ''}`} title={item.tooltip_key ? t(item.tooltip_key) : ''} arrow>
                            <Chip
                              label={getDisplayText(item, 'value', t)}
                              size="small"
                              sx={{ fontSize: '0.6rem', height: 22, fontWeight: 700, bgcolor: getDisplayTone(item.tone).bg, color: getDisplayTone(item.tone).color }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>

                    {riskFlagItems.length > 0 ? (
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: colors.gray[500], mb: 0.65, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {t('vpa.dialog.riskFlags')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                          {riskFlagItems.map((item, index) => (
                            <Chip key={`${item.value_key}-${index}`} label={getDisplayText(item, 'value', t)} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: getDisplayTone(item.tone).bg, color: getDisplayTone(item.tone).color, fontWeight: 700 }} />
                          ))}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              )}

              {chartData.length > 1 && (
                <ScoreHistoryCard
                  title={t('vpa.dialog.scoreHistory')}
                  chartData={chartData}
                  labels={scoreHistoryLabels}
                />
              )}

              {history.length > 0 && (
                <Box data-html2canvas-ignore="true" sx={{ borderRadius: 2, bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: colors.gray[50], borderBottom: `1px solid ${colors.gray[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.gray[600] }}>
                      {t('vpa.dialog.signalHistory', 'Signal history')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: colors.gray[400] }}>
                      {t('vpa.dialog.records', { count: history.length, defaultValue: '{{count}} records' })}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histDate', 'Date')}</TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histAction', 'Action')}</TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histSetup', 'Setup')}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histScore', 'Score')}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histClose', 'Close')}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.6rem', fontWeight: 700, color: colors.gray[500], textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: colors.gray[50] }}>{t('vpa.dialog.histVol', 'Vol×')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((entry, index) => {
                          const historyRowDisplay = entry.display?.history_row || {};
                          const actionDisplay = historyRowDisplay.action || null;
                          const setupDisplay = historyRowDisplay.setup || null;
                          const scoreDisplay = historyRowDisplay.score || null;
                          const volRatioDisplay = historyRowDisplay.vol_ratio || null;
                          const actionTone = getDisplayTone(actionDisplay?.tone);
                          const dateStr = (entry.scan_date || entry.date || '').toString().slice(5);
                          return (
                            <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: colors.gray[50] } }}>
                              <TableCell sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.66rem', fontFamily: 'monospace', color: colors.gray[500] }}>{dateStr}</Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: actionTone.color, bgcolor: actionTone.bg, px: 0.6, py: 0.1, borderRadius: 0.6, display: 'inline-block' }}>
                                  {getDisplayText(actionDisplay, 'value', t, '—')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.66rem', color: getDisplayTone(setupDisplay?.tone).color }}>{getDisplayText(setupDisplay, 'value', t)}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, fontFamily: 'monospace', color: getDisplayTone(scoreDisplay?.tone).color }}>
                                  {getDisplayText(scoreDisplay, 'value', t)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.66rem', fontFamily: 'monospace' }}>{fmt(entry.close)}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.4, px: 1 }}>
                                <Typography sx={{ fontSize: '0.66rem', fontFamily: 'monospace', color: getDisplayTone(volRatioDisplay?.tone).color }}>
                                  {getDisplayText(volRatioDisplay, 'value', t)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
